import React, {
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import { AppProvider } from "./context/AppContext";

import Navbar from "./components/Navbar";
import NavbarScroll from "./components/Navbar/NavbarScroll";
import WhatsAppIcon from "./components/WhatsAppIcon";

import HomePage from "./pages/HomePage";
import CategoriesPage from "./pages/CategoriesPage";
import ServicePage from "./pages/ServicePage";
import ContactPage from "./pages/ContactPage";
import ProductDetail from "./components/Categories/ProductDetail";

const Scroll = lazy(() => import("./components/Scroll"));
const Footer = lazy(() => import("./components/Footer"));

const LoginPage = lazy(() => import("./login/LoginPage"));
const Register = lazy(() => import("./login/Usuario/Register"));
const UpdatePasswordPage = lazy(() => import("./login/Usuario/UpdatePasswordPage"));
const UpdatePassworSystem = lazy(() => import("./login/UpdatePasswordPage"));

const ShopPage = lazy(() => import("./pages/ShopPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsAndConditionsPage = lazy(() => import("./pages/TermsAndConditionsPage"));
const OfferPage = lazy(() => import("./pages/OfferPage"));
const ClubPage = lazy(() => import("./pages/Club"));

const UserPanel = lazy(() => import("./login/Usuario/UserPanel"));
const AdminPanel = lazy(() => import("./login/Administrador/AdminPanel"));
const EnterprisePanel = lazy(() => import("./login/Empresa/EnterprisePanel"));
const NewInvoice = lazy(() => import("./login/Empresa/NuevaFactura/NewInvoice"));

const AdminSection = lazy(() => import("./login/Administrador/AdminSection"));
const Section = lazy(() => import("./login/Administrador/Section"));
const AdminProduct = lazy(() => import("./login/Administrador/AdminProduct"));
const Product = lazy(() => import("./login/Administrador/Product"));
const ProductDashboard = lazy(() => import("./login/Administrador/ProductForm/ProductDashboard"));
const Warehouses = lazy(() => import("./login/Administrador/ProductForm/Warehouses"));
const AdminProforma = lazy(() => import("./login/Administrador/AdminProforma"));
const Proforma = lazy(() => import("./login/Administrador/Proforma"));
const ProformaFacturacion = lazy(() => import("./login/Administrador/ProformaForm/ProformaFacturacion"));
const AdminClient = lazy(() => import("./login/Administrador/AdminClient"));
const Client = lazy(() => import("./login/Administrador/Client"));
const AdminVariant = lazy(() => import("./login/Administrador/AdminVariant"));
const Variant = lazy(() => import("./login/Administrador/Variant"));
const AdminPromotion = lazy(() => import("./login/Administrador/AdminPromotion"));
const Promotion = lazy(() => import("./login/Administrador/Promotion"));
const AdminServices = lazy(() => import("./login/Administrador/AdminServices"));
const Services = lazy(() => import("./login/Administrador/Services"));
const AdminClub = lazy(() => import("./login/Administrador/AdminClub"));
const Club = lazy(() => import("./login/Administrador/Club"));
const AdminInvoice = lazy(() => import("./login/Administrador/AdminInvoice"));
const Invoice = lazy(() => import("./login/Administrador/Invoice"));
const RegisterPurchase = lazy(() => import("./login/Administrador/RegisterPurchase"));
const AdminAccounts = lazy(() => import("./login/Administrador/AdminAccounts"));
const AdminMotion = lazy(() => import("./login/Administrador/AdminMotion"));
const Motion = lazy(() => import("./login/Administrador/Motion"));
const MotionDetail = lazy(() => import("./login/Administrador/MotionForm/MotionDetail"));
const AdminBank = lazy(() => import("./login/Administrador/AdminBank"));
const Bank = lazy(() => import("./login/Administrador/Bank"));
const Payment = lazy(() => import("./login/Administrador/Payment/Payment"));
const AdminEcommerce = lazy(() => import("./login/Administrador/AdminEcommerce"));
const Ecommerce = lazy(() => import("./login/Administrador/Ecommerce"));

const AppLayout = ({ children }) => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const lastState = useRef(false);

  const isAuthOrAdmin =
    location.pathname === "/login" ||
    location.pathname === "/system/update-password" ||
    location.pathname.startsWith("/admin-panel");

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onScroll = () => {
      const y = window.scrollY;

      if (!lastState.current && y > 160) {
        lastState.current = true;
        setScrolled(true);
      } else if (lastState.current && y < 80) {
        lastState.current = false;
        setScrolled(false);
      }
    };

    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!isAuthOrAdmin && (
        <>
          <div
            style={{
              opacity: scrolled ? 0 : 1,
              pointerEvents: scrolled ? "none" : "auto",
              transition: "opacity .28s ease",
            }}
          >
            <Navbar />
          </div>

          <div
            style={{
              position: "fixed",
              top: 8,
              left: "50%",
              transform: "translateX(-50%)",
              width: "90%",
              zIndex: 1200,
              opacity: scrolled ? 1 : 0,
              pointerEvents: scrolled ? "auto" : "none",
              transition: "opacity .28s ease",
            }}
          >
            <NavbarScroll />
          </div>
        </>
      )}

      <main style={{ flex: 1 }}>
        <Suspense fallback={null}>{children}</Suspense>
      </main>

      {!isAuthOrAdmin && (
        <>
          <WhatsAppIcon />
          <Suspense fallback={null}>
            <Scroll />
            <Footer />
          </Suspense>
        </>
      )}
    </div>
  );
};

const AppRoutes = () => {
  return (
    <AppLayout>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/registro" element={<Register />} />
        <Route path="/actualizar-clave" element={<UpdatePasswordPage />} />
        <Route path="/system/update-password" element={<UpdatePassworSystem />} />

        <Route path="/" element={<HomePage />} />
        <Route path="/servicios" element={<ServicePage />} />
        <Route path="/categorias" element={<CategoriesPage />} />
        <Route path="/contactos" element={<ContactPage />} />
        <Route path="/contacto" element={<ContactPage />} />

        <Route path="/categorias/:sectionSlug/:categorySlug" element={<CategoriesPage />} />
        <Route path="/categorias/:sectionSlug/:categorySlug/:productSlug" element={<ProductDetail />} />
        <Route path="/categorias/:sectionSlug/:categorySlug/:productSlug/:variantSlug" element={<ProductDetail />} />

        <Route path="/shop" element={<ShopPage />} />
        <Route path="/ofertas" element={<OfferPage />} />
        <Route path="/buscar" element={<ProductPage />} />
        <Route path="/politicas-privacidad" element={<PrivacyPolicyPage />} />
        <Route path="/terminos-condiciones" element={<TermsAndConditionsPage />} />
        <Route path="/club-hydroluz" element={<ClubPage />} />

        <Route path="/user-panel" element={<UserPanel />} />

        <Route path="/admin-panel/*" element={<AdminPanel />} />
        <Route path="/admin-panel/empresa" element={<EnterprisePanel />} />

        <Route path="/admin-panel/secciones" element={<AdminSection />} />
        <Route path="/admin-panel/secciones/nueva" element={<Section />} />

        <Route path="/admin-panel/proformas" element={<AdminProforma />} />
        <Route path="/admin-panel/proformas/nueva" element={<Proforma />} />
        <Route path="/admin-panel/proformas/editar/:id" element={<Proforma />} />
        <Route path="/admin-panel/proformas/facturacion/:id" element={<ProformaFacturacion />} />

        <Route path="/admin-panel/servicios" element={<AdminServices />} />
        <Route path="/admin-panel/servicios/nuevo" element={<Services />} />

        <Route path="/admin-panel/productoclub" element={<AdminClub />} />
        <Route path="/admin-panel/productoclub/nuevo" element={<Club />} />

        <Route path="/admin-panel/clientes" element={<AdminClient />} />
        <Route path="/admin-panel/clientes/nuevo" element={<Client />} />

        <Route path="/admin-panel/inventario/productos" element={<AdminProduct />} />
        <Route path="/admin-panel/inventario/productos/nuevo" element={<Product />} />
        <Route path="/admin-panel/inventario/productos/:id" element={<ProductDashboard />} />
        <Route path="/admin-panel/inventario/bodegas" element={<Warehouses />} />
        <Route path="/admin-panel/inventario/variantes" element={<AdminVariant />} />
        <Route path="/admin-panel/inventario/variantes/nueva" element={<Variant />} />
        <Route path="/admin-panel/inventario/movimientos" element={<AdminMotion />} />
        <Route path="/admin-panel/inventario/movimientos/nuevo" element={<Motion />} />
        <Route path="/admin-panel/inventario/movimientos/:id" element={<MotionDetail />} />

        <Route path="/admin-panel/promociones" element={<AdminPromotion />} />
        <Route path="/admin-panel/promociones/nueva" element={<Promotion />} />

        <Route path="/admin-panel/registro/documento" element={<NewInvoice />} />
        <Route path="/admin-panel/reporte/documento" element={<AdminInvoice />} />
        <Route path="/admin-panel/factura/:id" element={<Invoice />} />

        <Route path="/admin-panel/compras/registro" element={<RegisterPurchase />} />

        <Route path="/admin-panel/admin-cuentas" element={<AdminAccounts />} />
        <Route path="/admin-panel/bancos" element={<AdminBank />} />
        <Route path="/admin-panel/bancos/nuevo" element={<Bank />} />
        <Route path="/admin-panel/pagos/:id" element={<Payment />} />

        <Route path="/admin-panel/ecommerce" element={<AdminEcommerce />} />
        <Route path="/admin-panel/ecommerce/:ticket" element={<Ecommerce />} />
      </Routes>
    </AppLayout>
  );
};

const App = () => {
  return (
    <AppProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AppProvider>
  );
};

export default App;